import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import {
  balanceGameAudiences,
  balanceGameItems,
  balanceGameTags,
  type BalanceGameAudienceId,
  type BalanceGameItem,
  type BalanceGameTagId,
} from "../data/balanceGameItems";
import { shuffleArray } from "../utils/shuffleArray";

type Choice = "left" | "right";
type SelectionHistoryItem = {
  roundId: string;
  order: number;
  question: string;
  selectedOption: string;
  tagLabels: string[];
};

const DEFAULT_AUDIENCE_ID: BalanceGameAudienceId = "group";

const singleLineChipRowSx = {
  flexWrap: "nowrap",
  overflowX: "auto",
  overflowY: "hidden",
  pb: 0.25,
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
};

const getAudienceById = (audienceId: BalanceGameAudienceId) =>
  balanceGameAudiences.find((audience) => audience.id === audienceId) ??
  balanceGameAudiences[0];

const getTagById = (tagId: BalanceGameTagId) =>
  balanceGameTags.find((tag) => tag.id === tagId);

const getTagLabels = (tagIds: BalanceGameTagId[]) =>
  tagIds
    .map((tagId) => getTagById(tagId)?.label)
    .filter((label): label is string => Boolean(label));

const getAvailableTags = (audienceId: BalanceGameAudienceId) =>
  balanceGameTags.filter((tag) => tag.audienceIds.includes(audienceId));

const getFilteredBalanceItems = (
  audienceId: BalanceGameAudienceId,
  tagIds: BalanceGameTagId[],
) => {
  if (tagIds.length === 0) return [];

  const selectedTagSet = new Set(tagIds);

  return balanceGameItems.filter(
    (item) =>
      item.audienceIds.includes(audienceId) &&
      item.tagIds.some((tagId) => selectedTagSet.has(tagId)),
  );
};

const createBalanceRoundOrder = (items: BalanceGameItem[]) =>
  shuffleArray(items.map((item) => item.id));

const createInitialRoundOrder = () => {
  const initialAudience = getAudienceById(DEFAULT_AUDIENCE_ID);

  return createBalanceRoundOrder(
    getFilteredBalanceItems(DEFAULT_AUDIENCE_ID, initialAudience.defaultTagIds),
  );
};

export default function BalanceGamePage() {
  const navigate = useNavigate();
  const [selectedAudienceId, setSelectedAudienceId] =
    useState<BalanceGameAudienceId>(DEFAULT_AUDIENCE_ID);
  const [selectedTagIds, setSelectedTagIds] = useState<BalanceGameTagId[]>(
    () => getAudienceById(DEFAULT_AUDIENCE_ID).defaultTagIds,
  );
  const [roundOrder, setRoundOrder] = useState<string[]>(() =>
    createInitialRoundOrder(),
  );
  const [currentOrderPosition, setCurrentOrderPosition] = useState(0);
  const [selectedChoice, setSelectedChoice] = useState<Choice | null>(null);
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const [selectionHistory, setSelectionHistory] = useState<SelectionHistoryItem[]>(
    [],
  );

  const activeAudience = getAudienceById(selectedAudienceId);
  const availableTags = useMemo(
    () => getAvailableTags(selectedAudienceId),
    [selectedAudienceId],
  );
  const activeBalanceItems = useMemo(
    () => getFilteredBalanceItems(selectedAudienceId, selectedTagIds),
    [selectedAudienceId, selectedTagIds],
  );
  const activeRoundById = useMemo(
    () => new Map(activeBalanceItems.map((item) => [item.id, item])),
    [activeBalanceItems],
  );
  const [usedCount, setUsedCount] = useState(
    roundOrder.length > 0 ? 1 : 0,
  );

  const currentRoundId = roundOrder[currentOrderPosition];
  const currentRound = currentRoundId
    ? activeRoundById.get(currentRoundId)
    : undefined;
  const hasRounds = roundOrder.length > 0 && Boolean(currentRound);
  const isCompleted = hasRounds && usedCount >= roundOrder.length;
  const selectedTagLabels = getTagLabels(selectedTagIds);
  const selectedTagSummary =
    selectedTagLabels.length > 0
      ? selectedTagLabels.join(", ")
      : "선택된 태그 없음";
  const currentRoundTagLabels = currentRound
    ? getTagLabels(currentRound.tagIds)
    : [];
  const isAllAvailableTagsSelected =
    availableTags.length > 0 &&
    availableTags.every((tag) => selectedTagIds.includes(tag.id));

  const rebuildBalanceGame = (
    nextAudienceId: BalanceGameAudienceId,
    nextTagIds: BalanceGameTagId[],
  ) => {
    const nextItems = getFilteredBalanceItems(nextAudienceId, nextTagIds);

    setRoundOrder(createBalanceRoundOrder(nextItems));
    setCurrentOrderPosition(0);
    setSelectedChoice(null);
    setIsHistoryExpanded(false);
    setSelectionHistory([]);
    setUsedCount(nextItems.length > 0 ? 1 : 0);
  };

  const handleAudienceChange = (audienceId: BalanceGameAudienceId) => {
    if (audienceId === selectedAudienceId) return;

    const nextTagIds = getAudienceById(audienceId).defaultTagIds;

    setSelectedAudienceId(audienceId);
    setSelectedTagIds(nextTagIds);
    rebuildBalanceGame(audienceId, nextTagIds);
  };

  const handleTagToggle = (tagId: BalanceGameTagId) => {
    const nextTagIds = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((selectedTagId) => selectedTagId !== tagId)
      : [...selectedTagIds, tagId];

    setSelectedTagIds(nextTagIds);
    rebuildBalanceGame(selectedAudienceId, nextTagIds);
  };

  const selectAllAvailableTags = () => {
    const nextTagIds = availableTags.map((tag) => tag.id);

    setSelectedTagIds(nextTagIds);
    rebuildBalanceGame(selectedAudienceId, nextTagIds);
  };

  const resetDefaultTags = () => {
    const nextTagIds = activeAudience.defaultTagIds;

    setSelectedTagIds(nextTagIds);
    rebuildBalanceGame(selectedAudienceId, nextTagIds);
  };

  const handleChoiceSelect = (choice: Choice) => {
    if (!currentRound) return;

    const nextSelectedOption =
      choice === "left" ? currentRound.leftLabel : currentRound.rightLabel;

    setSelectedChoice(choice);
    setSelectionHistory((previousHistory) => [
      {
        roundId: currentRound.id,
        order: usedCount,
        question: currentRound.question,
        selectedOption: nextSelectedOption,
        tagLabels: currentRoundTagLabels,
      },
      ...previousHistory.filter((item) => item.roundId !== currentRound.id),
    ]);
  };

  const moveToNextRound = () => {
    if (!hasRounds || isCompleted) return;

    setCurrentOrderPosition((value) => value + 1);
    setSelectedChoice(null);
    setUsedCount((value) => value + 1);
  };

  const resetBalanceGame = () => {
    setCurrentOrderPosition(0);
    setSelectedChoice(null);
    setIsHistoryExpanded(false);
    setSelectionHistory([]);
    setUsedCount(roundOrder.length > 0 ? 1 : 0);
  };

  const reshuffleBalanceGame = () => {
    const nextRoundOrder = createBalanceRoundOrder(activeBalanceItems);

    setRoundOrder(nextRoundOrder);
    setCurrentOrderPosition(0);
    setSelectedChoice(null);
    setIsHistoryExpanded(false);
    setSelectionHistory([]);
    setUsedCount(nextRoundOrder.length > 0 ? 1 : 0);
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={4} sx={{ p: 3, mt: 4, borderRadius: 3 }}>
        <Stack spacing={3}>
          <Stack spacing={1} textAlign="center">
            <Typography variant="h4">⚖️ 밸런스 게임</Typography>
            <Typography color="text.secondary">
              인원과 태그에 맞는 질문만 골라 둘 중 하나를 선택해보세요.
            </Typography>
          </Stack>

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                진행 타입
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={singleLineChipRowSx}
              >
                {balanceGameAudiences.map((audience) => (
                  <Button
                    key={audience.id}
                    size="small"
                    color={audience.accentColor}
                    variant={
                      audience.id === selectedAudienceId
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() => handleAudienceChange(audience.id)}
                    sx={{
                      flex: {
                        xs: "1 1 calc(33.333% - 8px)",
                        sm: "1 1 calc(33.333% - 8px)",
                      },
                      minWidth: 0,
                    }}
                  >
                    {audience.shortLabel}
                  </Button>
                ))}
              </Stack>

              <Typography variant="body2" color="text.secondary">
                {activeAudience.description}
              </Typography>
            </Stack>
          </Paper>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={singleLineChipRowSx}
          >
            <Chip
              size="small"
              color={activeAudience.accentColor}
              label={activeAudience.label}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`질문 ${roundOrder.length}개`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`태그 ${selectedTagIds.length}개`}
            />
            {hasRounds && (
              <Chip
                size="small"
                variant="outlined"
                label={`ROUND ${usedCount} / ${roundOrder.length}`}
              />
            )}
          </Stack>

          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
            {currentRound ? (
              <Stack spacing={1.5} textAlign="center">
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  justifyContent="center"
                  sx={singleLineChipRowSx}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`ROUND ${usedCount}`}
                  />
                  {currentRoundTagLabels.slice(0, 3).map((label) => (
                    <Chip key={label} size="small" label={label} />
                  ))}
                </Stack>
                <Typography variant="h5">{currentRound.question}</Typography>
              </Stack>
            ) : (
              <Stack spacing={1.25} textAlign="center">
                <Typography variant="h6">
                  선택된 태그로 진행할 질문이 없어요.
                </Typography>
                <Typography color="text.secondary">
                  태그 설정에서 하나 이상 선택하거나 기본 태그로 되돌려보세요.
                </Typography>
              </Stack>
            )}
          </Paper>

          {currentRound && (
            <Stack spacing={2}>
              <Button
                fullWidth
                size="large"
                variant={selectedChoice === "left" ? "contained" : "outlined"}
                onClick={() => handleChoiceSelect("left")}
                sx={{ py: 2 }}
              >
                A. {currentRound.leftLabel}
              </Button>

              <Button
                fullWidth
                size="large"
                variant={selectedChoice === "right" ? "contained" : "outlined"}
                onClick={() => handleChoiceSelect("right")}
                sx={{ py: 2 }}
              >
                B. {currentRound.rightLabel}
              </Button>
            </Stack>
          )}

          <Accordion
            expanded={isSettingsExpanded}
            onChange={(_, expanded) => setIsSettingsExpanded(expanded)}
            disableGutters
            sx={{
              borderRadius: 3,
              bgcolor: "background.default",
              "&:before": { display: "none" },
              overflow: "hidden",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack spacing={0.25}>
                <Typography variant="subtitle1" fontWeight={600}>
                  설정
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  태그 {selectedTagIds.length}개, 선택 기록{" "}
                  {selectionHistory.length}개
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Stack spacing={3}>
                <Stack spacing={2}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      태그 설정
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedTagSummary}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" color="text.secondary">
                    {activeAudience.label} 타입에서 사용할 태그를 선택하면, 해당
                    태그가 붙은 질문만 새 순서로 섞입니다.
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{ flexWrap: "wrap" }}
                  >
                    {availableTags.map((tag) => {
                      const isSelected = selectedTagIds.includes(tag.id);
                      const questionCount = getFilteredBalanceItems(
                        selectedAudienceId,
                        [tag.id],
                      ).length;

                      return (
                        <Chip
                          key={tag.id}
                          clickable
                          color={
                            isSelected ? activeAudience.accentColor : "default"
                          }
                          variant={isSelected ? "filled" : "outlined"}
                          label={`${tag.label} ${questionCount}`}
                          onClick={() => handleTagToggle(tag.id)}
                        />
                      );
                    })}
                  </Stack>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      color={activeAudience.accentColor}
                      onClick={resetDefaultTags}
                    >
                      기본 태그
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color={activeAudience.accentColor}
                      onClick={selectAllAvailableTags}
                      disabled={isAllAvailableTagsSelected}
                    >
                      전체 선택
                    </Button>
                  </Stack>
                </Stack>

                <Divider />

                <Accordion
                  expanded={isHistoryExpanded}
                  onChange={(_, expanded) => setIsHistoryExpanded(expanded)}
                  disableGutters
                  sx={{
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    "&:before": { display: "none" },
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack spacing={0.25}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        선택 기록
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectionHistory.length > 0
                          ? `${selectionHistory.length}개 답변을 다시 확인할 수 있어요.`
                          : "아직 선택 기록이 없어요."}
                      </Typography>
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails>
                    {selectionHistory.length > 0 ? (
                      <Stack spacing={1.25}>
                        {selectionHistory.map((item) => (
                          <Paper
                            key={item.roundId}
                            variant="outlined"
                            sx={{ p: 1.75, borderRadius: 2 }}
                          >
                            <Stack spacing={0.5}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                질문 {item.order}
                              </Typography>
                              <Typography fontWeight={600}>
                                {item.question}
                              </Typography>
                              <Typography color="text.secondary">
                                선택: {item.selectedOption}
                              </Typography>
                              {item.tagLabels.length > 0 && (
                                <Stack
                                  direction="row"
                                  spacing={0.75}
                                  useFlexGap
                                  sx={singleLineChipRowSx}
                                >
                                  {item.tagLabels.slice(0, 3).map((label) => (
                                    <Chip
                                      key={label}
                                      size="small"
                                      variant="outlined"
                                      label={label}
                                    />
                                  ))}
                                </Stack>
                              )}
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      <Typography color="text.secondary">
                        먼저 마음에 드는 쪽을 고르면 여기에 기록됩니다.
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {isCompleted && (
            <Paper
              variant="outlined"
              sx={{ p: 2.5, borderRadius: 3, textAlign: "center" }}
            >
              <Stack spacing={1}>
                <Typography fontWeight={600}>
                  모든 밸런스 게임을 다 사용했어요.
                </Typography>
                <Typography color="text.secondary">
                  다시 섞어서 처음부터 새 순서로 시작하거나 게임 목록으로
                  돌아갈 수 있어요.
                </Typography>
              </Stack>
            </Paper>
          )}

          <Stack spacing={2.5}>
            {hasRounds && !isCompleted && (
              <Button
                fullWidth
                size="large"
                variant="contained"
                onClick={moveToNextRound}
              >
                다음 밸런스 게임
              </Button>
            )}

            {hasRounds && isCompleted && (
              <Button
                fullWidth
                size="large"
                variant="contained"
                onClick={reshuffleBalanceGame}
              >
                다시 섞어서 처음부터
              </Button>
            )}

            {!hasRounds && (
              <Button fullWidth size="large" variant="contained" disabled>
                태그를 선택해주세요
              </Button>
            )}

            <Stack spacing={1.5} sx={{ pt: !isCompleted ? 0.5 : 0 }}>
              {hasRounds && !isCompleted && (
                <Button
                  fullWidth
                  size="large"
                  variant="outlined"
                  onClick={resetBalanceGame}
                >
                  전체 재시작
                </Button>
              )}

              <Button fullWidth size="large" onClick={() => navigate("/list")}>
                게임 목록으로
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </Container>
  );
}
